/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { SidebarProvider, useSidebar } from "../context/SidebarContext";
import { Outlet, useNavigate } from "react-router";
import { useMemo, useState, useCallback, useEffect, ReactNode } from "react";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import SubscriptionGuard from "../components/auth/SubscriptionGuard";
import { useAuth } from "../context/AuthContext";
import axios from "../lib/axios";
import Input from "../components/form/input/InputField";
import Label from "../components/form/Label";
import FloatingAskNetguruButton from "../components/ask-netguru/FloatingAskNetguruButton";
import AskNetguruDrawer from "../components/ask-netguru/AskNetguruDrawer";
import CredentialsModal from "../components/auth/CredentialsModal";
import PremiumChatWidget from "../components/ask-netguru/AskNetguruChat";
import ChatWidget from "../components/chatbot/ChatWidget";
import { MasterSyncProvider } from "../context/MasterSyncContext";
import SyncProgressBar from "../components/common/SyncProgressBar";
import Footer from "../components/footer/Footer";
import { CountdownModal } from "../pages/DeleteandDeactive/DeleteandDeactive";
import { useUserActivity } from "../hooks/useUserActivity";

type ShopifyScopeStatus = {
  requires_update: boolean;
  shop?: string;
  missing_scopes?: string[];
  reauthorize_url?: string | null;
};

const LayoutContent: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const {
    stores,
    refreshStores,
    currentSubscription,
    currentShop,
    loadSubscriptionData,
    user,
    checkAuth,
  } = useAuth();

  // Use user activity tracking
  useUserActivity();
  const navigate = useNavigate();
  const [installing, setInstalling] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [isAskNetguruOpen, setIsAskNetguruOpen] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [storeError, setStoreError] = useState("");
  const [validating, setValidating] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const [scopeStatus, setScopeStatus] = useState<ShopifyScopeStatus | null>(null);
  const [checkingScopes, setCheckingScopes] = useState(false);
  const [updatingScopes, setUpdatingScopes] = useState(false);
  const pendingStores = useMemo(
    () => stores.filter((store) => !store.connected),
    [stores],
  );

  const connectedStores = useMemo(
    () => stores.filter((store) => store.connected),
    [stores],
  );

  // Only show install banner if there are no connected stores
  const needsInstall = connectedStores.length === 0;

  // For display, prefer showing connected stores, but for installation banner, show pending stores
  const highlightedStore = pendingStores[0] ?? stores[0] ?? null;
  const highlightedStoreLabel = highlightedStore
    ? highlightedStore.shop.replace(".myshopify.com", "")
    : null;

  // Get platform type from highlighted store
  const platformType = highlightedStore?.platform_type || 'shopify';

  // Get the current connected store for display purposes
  const currentConnectedStore = useMemo(() => {
    return (
      connectedStores.find(
        (store) =>
          store.shop === stores.find((s) => s.shop === store.shop)?.shop,
      ) ??
      connectedStores[0] ??
      null
    );
  }, [connectedStores, stores]);

  const normalizeShop = (value: string) => {
    let cleaned = value.trim().toLowerCase();
    if (!cleaned) return "";
    cleaned = cleaned.replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!cleaned.endsWith(".myshopify.com")) {
      cleaned = cleaned.includes(".") ? cleaned : `${cleaned}.myshopify.com`;
    }
    return cleaned;
  };

  const handleConnectStoreClick = useCallback(() => {
    if (highlightedStore) {
      setStoreName(highlightedStore.shop.replace(".myshopify.com", ""));
    } else {
      setStoreName("");
    }
    setStoreError("");
    setShowConnectModal(true);
  }, [highlightedStore]);

  // Listen for custom event to open connect store modal from dashboard
  useEffect(() => {
    const handleOpenModal = () => {
      setStoreName("");
      setStoreError("");
      setShowConnectModal(true);
    };

    window.addEventListener("openConnectStoreModal", handleOpenModal);
    return () => {
      window.removeEventListener("openConnectStoreModal", handleOpenModal);
    };
  }, []);

  // Load subscription data when component mounts or currentShop changes
  useEffect(() => {
    if (currentShop) {
      loadSubscriptionData(currentShop);
    }
  }, [currentShop]);

  useEffect(() => {
    if (!user || !currentShop || connectedStores.length === 0) {
      setScopeStatus(null);
      return;
    }

    let isMounted = true;
    setCheckingScopes(true);

    axios
      .get<ShopifyScopeStatus>("/shopify/check-scopes", {
        params: { shop: currentShop },
      })
      .then((response) => {
        if (isMounted) {
          setScopeStatus(response.data);
        }
      })
      .catch((error) => {
        console.error("Failed to check Shopify scope requirements", error);
        if (isMounted) {
          setScopeStatus(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setCheckingScopes(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user, currentShop, connectedStores.length]);

  const handleUpdatePermissions = useCallback(() => {
    if (!scopeStatus?.reauthorize_url) {
      return;
    }

    setUpdatingScopes(true);
    window.location.href = scopeStatus.reauthorize_url;
  }, [scopeStatus?.reauthorize_url]);

  // Show password setup modal if user needs to set password
  useEffect(() => {
    if (user?.password_reset_required && !showPasswordModal) {
      setShowPasswordModal(true);
    }
  }, [user?.password_reset_required, showPasswordModal]);

  const handlePasswordModalClose = async () => {
    setShowPasswordModal(false);
    // Refresh auth to check if password was set
    await checkAuth();
  };
 
   
  // Global Shopify uninstall detection
  useEffect(() => {
    if (user?.pending_delete && user?.uninstalled) {
      setShowCountdownModal(true);
    }
  }, [user]);

  // Handle countdown completion - finalize delete and logout
  const handleCountdownComplete = async () => {
    try {
      await axios.post("/finalize-delete");
    } catch (error) {
      console.error("Failed to finalize delete:", error);
    } finally {
      // Safe logout
      if (typeof window !== 'undefined') {
        localStorage.setItem('is_logging_out', 'true');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('shop');
        localStorage.removeItem('stores');
        localStorage.removeItem('activeStoreId');
        sessionStorage.clear();
        localStorage.removeItem('auth_event');
        localStorage.setItem(
          'auth_event',
          JSON.stringify({
            type: 'logout',
            at: Date.now(),
          })
        );
        setTimeout(() => {
          localStorage.removeItem('is_logging_out');
          window.location.href = '/signin';
        }, 200);
      }
    }
  };

  const handleConnectStore = useCallback(async () => {
    if (installing || validating) {
      return;
    }

    if (!storeName.trim()) {
      setStoreError("Please enter your Shopify store URL.");
      return;
    }

    setStoreError("");
    setValidating(true);

    try {
      const normalizedShop = normalizeShop(storeName);
      if (!normalizedShop) {
        setStoreError("Please enter a valid Shopify store URL.");
        setValidating(false);
        return;
      }

      // Validate shop exists
      const validateRes = await axios.post("/validate-shop", {
        shop: normalizedShop,
      });
      if (!validateRes.data.exists) {
        setStoreError(
          "That Shopify store could not be found. Please check the domain.",
        );
        setValidating(false);
        return;
      }

      setInstalling(true);
      const response = await axios.post("/register-shop", {
        shop: normalizedShop,
      });

      const installUrl = response.data?.install_url;
      if (typeof installUrl === "string" && installUrl.length > 0) {
        // Close modal and refresh stores before redirecting
        setShowConnectModal(false);
        await refreshStores();
        window.location.href = installUrl;
        return;
      }

      setStoreError("Failed to retrieve install URL. Please try again.");
      setInstalling(false);
      setValidating(false);
    } catch (error: any) {
      console.error("Failed to initiate Shopify install", error);
      setStoreError(
        error.response?.data?.message ||
        "Failed to initiate shop installation. Please try again.",
      );
      setInstalling(false);
      setValidating(false);
    }
  }, [storeName, installing, validating, refreshStores]);

  return (
    <div className="min-h-screen">
      <div>
        <AppSidebar disableNavigation={needsInstall} />
        <Backdrop />
      </div>
      <div
        className={`min-h-screen flex flex-col transition-all duration-300 ease-in-out ${isExpanded || isHovered ? "lg:ml-[212px]" : "lg:ml-[64px]"
          } ${isMobileOpen ? "ml-0" : ""}`}
      >
        <AppHeader />
        <SyncProgressBar />
        {/* max-w-dashboard overflow-x-hidden*/}
        <div className="mx-auto w-full flex-1 px-4 pb-10 pt-3 sm:px-5">
          {scopeStatus?.requires_update && scopeStatus.reauthorize_url && (
            <div className="neo-card mb-3 px-4 py-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="neo-eyebrow">
                    Shopify permissions update required
                  </p>
                  <p className="mt-0.5 text-[13px] text-[var(--ink)]">
                    New permissions are required to keep your Shopify data syncing correctly.
                  </p>
                  {/* {scopeStatus.missing_scopes && scopeStatus.missing_scopes.length > 0 && (
                    <p className="mt-0.5 text-xs text-[var(--accent2)] dark:text-[var(--accent2)]">
                      Missing scopes: {scopeStatus.missing_scopes.join(", ")}
                    </p>
                  )} */}
                </div>
                <button
                  type="button"
                  onClick={handleUpdatePermissions}
                  disabled={checkingScopes || updatingScopes}
                  className="neo-btn shrink-0"
                >
                  {updatingScopes ? "Redirecting..." : "Update Permissions"}
                </button>
              </div>
            </div>
          )}

          {needsInstall && (
            <div className="neo-card mb-6 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="neo-eyebrow text-[var(--critt)]">
                    Installation incomplete
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--ink)]">
                    Connect your store to get started with Neo. You can add both Shopify and WooCommerce stores.
                  </p>
                  {highlightedStore && (
                    <span className="mt-1 block text-[11.5px] text-[var(--mute)]">
                      Pending store: {highlightedStore.shop} ({platformType === 'woocommerce' ? 'WooCommerce' : 'Shopify'})
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <button
                    type="button"
                    onClick={handleConnectStoreClick}
                    disabled={installing}
                    className="neo-btn"
                  >
                    Connect Shopify
                  </button>
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--mute2)] sm:hidden">or</span>
                  <button
                    type="button"
                    onClick={() => navigate('/on-boarding?platform=woocommerce')}
                    disabled={installing}
                    className="neo-btn"
                  >
                    Connect WooCommerce
                  </button>
                </div>
              </div>
            </div>
          )}

          {children ?? <Outlet />}
        </div>
        <Footer />
      </div>

      {/* <PremiumChatWidget /> */}
      <ChatWidget />

      {/* Connect Store Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4">
          <div className="neo-modal w-full max-w-md p-6">
            <h2 className="mb-1 text-[23px] font-extrabold leading-tight text-[var(--ink)]">
              Connect Shopify Store
            </h2>
            <p className="mb-4 text-[13px] text-[var(--ink2)]">
              Enter your Shopify store URL to connect and install the app.
            </p>
            {storeError && (
              <div className="mb-4 rounded-[10px] border border-[var(--crit)]/40 bg-[var(--critbg)] p-3 text-[11.5px] text-[var(--critt)]" role="alert">
                {storeError}
              </div>
            )}
            <div className="mb-4">
              <Label>
                Shopify Store URL<span className="text-error-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="merchant.myshopify.com or store name"
                value={storeName}
                onChange={(e) => {
                  setStoreName(e.target.value);
                  setStoreError("");
                }}
                disabled={installing || validating}
                className="w-full"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowConnectModal(false);
                  setStoreName("");
                  setStoreError("");
                }}
                disabled={installing || validating}
                className="neo-btn neo-btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleConnectStore}
                disabled={installing || validating || !storeName.trim()}
                className="neo-btn"
              >
                {installing
                  ? "Redirecting..."
                  : validating
                    ? "Validating..."
                    : "Connect & Install"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Setup Modal - shown when password_reset_required is true */}
      {showPasswordModal && (
        <CredentialsModal
          onClose={handlePasswordModalClose}
          passwordOnly={true}
        />
      )}

      <CountdownModal
        open={showCountdownModal}
        message="Your account has been deleted"
        onComplete={handleCountdownComplete}
      />
    </div>
  );
};

export const AppLayoutContainer: React.FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <SidebarProvider>
    <MasterSyncProvider>
      <LayoutContent>{children}</LayoutContent>
    </MasterSyncProvider>
  </SidebarProvider>
);

const AppLayout: React.FC = () => {
  return (
    <ProtectedRoute>
      <SubscriptionGuard>
        <AppLayoutContainer />
      </SubscriptionGuard>
    </ProtectedRoute>
  );
};

export default AppLayout;
